import { useState } from 'react';

import { Button } from '../../../../../components/ui/Button';
import { Dialog } from '../../../../../components/ui/Dialog';
import { Input } from '../../../../../components/ui/Input';
import { Link } from '../../../../../components/ui/Link';
import { Radio } from '../../../../../components/ui/Radio';
import { Select } from '../../../../../components/ui/Select';
import { Tag } from '../../../../../components/ui/Tag';
import type { Coupon, CouponGroup } from '../fixtures';
import { entityById, pairSources, relatedCoupons } from '../stackRules';
import styles from './CouponManagementPage.module.css';
import { CouponPickerDialog } from './CouponPickerDialog';

export type RuleSaveInput =
  | { kind: 'single'; mainId: number; specified: readonly number[]; stackMode: 'global' | 'specified' }
  | { couponIds: readonly number[]; groupId?: number; kind: 'group'; name: string };

export interface RuleEditorTarget {
  focusId?: number;
  key?: string;
}

interface RuleEditorDialogProps {
  editing: RuleEditorTarget;
  entities: readonly Coupon[];
  groups: readonly CouponGroup[];
  onNotice: (message: string) => void;
  onOpenChange: (open: boolean) => void;
  onSave: (input: RuleSaveInput) => void;
}

interface Draft {
  groupIds: readonly number[];
  groupName: string;
  kind: 'single' | 'group';
  mainId: number | null;
  specified: readonly number[];
  stackMode: '' | 'global' | 'specified';
}

type PickContext = 'main' | 'specified' | 'group';

function initDraft(editing: RuleEditorTarget, entities: readonly Coupon[], groups: readonly CouponGroup[]): Draft {
  const empty: Draft = { kind: 'single', mainId: null, stackMode: '', specified: [], groupName: '', groupIds: [] };

  if (editing.key) {
    const [type, rawId] = editing.key.split('-');
    const id = Number(rawId);
    if (type === 'single') {
      const coupon = entityById(entities, id);
      return { ...empty, mainId: coupon?.id ?? null, stackMode: coupon?.stackMode === 'specified' ? 'specified' : 'global', specified: coupon?.specified ?? [] };
    }
    const group = groups.find((item) => item.id === id);
    return { ...empty, kind: 'group', groupName: group?.name ?? '', groupIds: group?.couponIds ?? [] };
  }

  if (editing.focusId != null) {
    const coupon = entityById(entities, editing.focusId);
    return { ...empty, mainId: coupon?.id ?? null, stackMode: coupon && coupon.stackMode !== 'none' ? coupon.stackMode : '', specified: coupon?.specified ?? [] };
  }

  return empty;
}

function previewTag(coupon: Coupon) {
  return <Tag category="blue" key={coupon.id}>{coupon.name}</Tag>;
}

function sameIds(left: readonly number[], right: readonly number[]): boolean {
  if (left.length !== right.length) return false;
  const set = new Set(left);
  return right.every((id) => set.has(id));
}

function isDirty(draft: Draft, initial: Draft): boolean {
  return draft.kind !== initial.kind
    || draft.mainId !== initial.mainId
    || draft.stackMode !== initial.stackMode
    || draft.groupName !== initial.groupName
    || !sameIds(draft.specified, initial.specified)
    || !sameIds(draft.groupIds, initial.groupIds);
}

export function RuleEditorDialog({ editing, entities, groups, onNotice, onOpenChange, onSave }: RuleEditorDialogProps) {
  const [draft, setDraft] = useState(() => initDraft(editing, entities, groups));
  const [initial] = useState(() => initDraft(editing, entities, groups));
  const [pickContext, setPickContext] = useState<PickContext | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [scopeSearch, setScopeSearch] = useState('');
  const { groupIds, groupName, kind, mainId, specified, stackMode } = draft;
  const dirty = isDirty(draft, initial);

  const globalCoupons = entities.filter((coupon) => coupon.globalStackingEligible !== false);
  const mainLocked = Boolean(editing.key) || editing.focusId != null;
  const mainCoupon = mainId != null ? entityById(entities, mainId) : undefined;

  const currentEntities: readonly Coupon[] = kind === 'single'
    ? (mainCoupon == null ? [] : stackMode === 'global' ? globalCoupons.filter((coupon) => coupon.id !== mainId) : specified.map((id) => entityById(entities, id)).filter((coupon): coupon is Coupon => Boolean(coupon)))
    : groupIds.map((id) => entityById(entities, id)).filter((coupon): coupon is Coupon => Boolean(coupon));

  const relatedToMain = mainCoupon ? relatedCoupons(mainCoupon.id, entities, groups) : [];
  const existing = kind === 'single' && stackMode === 'specified' && mainCoupon
    ? relatedToMain.filter((other) => pairSources(mainCoupon.id, other.id, entities, groups).some((source) => !source.startsWith(mainCoupon.name)))
    : [];

  function chooseKind(next: 'single' | 'group') {
    setDraft((current) => ({ ...current, kind: next, specified: next === 'group' ? [] : current.specified }));
  }

  function chooseStackMode(next: 'global' | 'specified') {
    setDraft((current) => ({ ...current, stackMode: next, specified: next === 'global' ? [] : current.specified }));
  }

  function handleSave(): boolean {
    if (kind === 'single') {
      if (mainCoupon == null || stackMode === '') return false;
      if (stackMode === 'specified' && specified.length === 0) return false;
      onSave({ kind: 'single', mainId: mainCoupon.id, stackMode, specified: stackMode === 'specified' ? [...specified] : [] });
    } else {
      if (!groupName.trim() || groupIds.length < 2) return false;
      const groupId = editing.key?.startsWith('group-') ? Number(editing.key.split('-')[1]) : undefined;
      onSave({ kind: 'group', name: groupName.trim(), couponIds: [...groupIds], groupId });
    }
    return true;
  }

  function requestClose() {
    if (dirty) {
      setConfirmOpen(true);
      return;
    }
    onOpenChange(false);
  }

  function removeCoupon(id: number) {
    setDraft((current) => ({
      ...current,
      groupIds: current.groupIds.filter((value) => value !== id),
      specified: current.specified.filter((value) => value !== id),
    }));
    onNotice('已移除一张优惠券');
  }



  function selectedTag(coupon: Coupon) {
    return (
      <Tag category="blue" closable closeLabel={`移除${coupon.name}`} key={coupon.id} onClose={() => removeCoupon(coupon.id)}>
        {coupon.name}
      </Tag>
    );
  }

  function renderSelectedScope() {
    const query = scopeSearch.trim();
    const visibleScopeEntities = query ? currentEntities.filter((coupon) => coupon.name.includes(query)) : currentEntities;

    return <>
      {currentEntities.length > 0 ? <Input ariaLabel="搜索已选优惠券" className={styles.ruleScopeSearch} onValueChange={setScopeSearch} placeholder="搜索已选优惠券" value={scopeSearch} /> : null}
      <div className={styles.ruleScopeTags}>{visibleScopeEntities.length > 0 ? visibleScopeEntities.map(selectedTag) : <span className={styles.muted}>暂未选择优惠券</span>}</div>
      {query && visibleScopeEntities.length === 0 ? <p className={styles.fieldHelp}>未找到“{query}”，该券未被选择。</p> : null}
    </>;
  }

  const mainOptions = entities
    .filter((coupon) => coupon.stackMode === 'none' || coupon.id === mainId)
    .map((coupon) => ({ label: coupon.name, value: String(coupon.id) }));
  const mainPickerBase = entities.filter((coupon) => coupon.stackMode === 'none' || coupon.id === mainId);
  const pickerBase = pickContext === 'main' ? mainPickerBase : entities;
  const pickerSelected = pickContext === 'main' ? (mainId == null ? [] : [mainId]) : pickContext === 'group' ? groupIds : specified;

  return <>
    <Dialog
      footer={<><Button onClick={requestClose} type="default">取消</Button><Button onClick={() => { handleSave(); }}>保存规则</Button></>}
      onOpenChange={(next) => { if (!next) requestClose(); }}
      open
      side="right"
      title={editing.key ? '编辑规则' : '新增规则'}
      width={560}
    >
      <div className={styles.ruleEditor}>
        {!editing.key ? <div className={styles.ruleRow}>
          <div className={`${styles.ruleLabel} ${styles.required}`}>规则类型：</div>
          <div className={styles.ruleValue}>
            <div aria-label="规则类型" className={styles.ruleRadios} role="radiogroup">
              <Radio checked={kind === 'single'} label="单券指定叠加" name="rule-kind" onCheckedChange={() => chooseKind('single')} value="single" />
              <Radio checked={kind === 'group'} label="多券组合叠加" name="rule-kind" onCheckedChange={() => chooseKind('group')} value="group" />
            </div>
            <p className={styles.fieldHelp}>单券指定叠加：为一张券设置全局或指定叠加；多券组合叠加：组内多张券相互叠加。</p>
          </div>
        </div> : null}

        {kind === 'single'
          ? <>
            <div className={styles.ruleRow}>
              <div className={`${styles.ruleLabel} ${styles.required}`}>主券：</div>
              <div className={styles.ruleValue}>
                <Select ariaLabel="主券" className={styles.mainSelect} disabled={mainLocked} mode="external" onOpen={() => setPickContext('main')} onValueChange={() => undefined} options={mainOptions} value={mainId != null ? String(mainId) : ''} />
                {mainCoupon ? null : <p className={styles.fieldHelp}>主券只表示规则发起方，叠加关系双向生效，不决定优惠券使用或抵扣的先后顺序。</p>}
              </div>
            </div>
            <div className={styles.ruleRow}>
              <div className={`${styles.ruleLabel} ${styles.required}`}>叠加方式：</div>
              <div className={styles.ruleValue}>
                <div aria-label="叠加方式" className={styles.ruleRadios} role="radiogroup">
                  <Radio checked={stackMode === 'global'} label="与全部优惠券叠加" name="rule-stack-mode" onCheckedChange={() => chooseStackMode('global')} value="global" />
                  <Radio checked={stackMode === 'specified'} label="仅与指定券叠加" name="rule-stack-mode" onCheckedChange={() => chooseStackMode('specified')} value="specified" />
                </div>
              </div>
            </div>
            <div className={styles.singleRuleCards}>
              <section className={styles.ruleScopeCard}>
                <div className={styles.ruleScopeCardHeader}>
                  <div>
                    <h2>本次规则范围</h2>
                    <p>{mainCoupon == null
                      ? '请选择主券后查看本次规则范围。'
                      : stackMode === ''
                        ? '请选择叠加方式后查看本次规则范围。'
                        : stackMode === 'global'
                          ? `与全部优惠券叠加，包含后续新增的优惠券（当前 ${currentEntities.length} 张）。`
                          : `已选择 ${currentEntities.length} 张可叠加优惠券${currentEntities.length < 1 ? '，至少选择1张优惠券。' : '。'}`}</p>
                  </div>
                  {stackMode === 'specified' ? <Link aria-label="选择优惠券" className={styles.ruleScopeSelect} onClick={() => setPickContext('specified')}>＋选择优惠券</Link> : null}
                </div>
                {mainCoupon && stackMode !== '' ? stackMode === 'specified' ? renderSelectedScope() : <div className={styles.ruleScopeTags}>{currentEntities.length > 0 ? currentEntities.map(previewTag) : <span className={styles.muted}>暂未选择优惠券</span>}</div> : null}
              </section>
              {stackMode === 'specified' ? <section className={styles.ruleScopeCard}>
                <div className={styles.ruleScopeCardHeader}>
                  <div>
                    <h2>已有其他关系</h2>
                    <p>{mainCoupon ? '其他单券规则或组合规则中已包含该主券，不影响本次设置。' : '请选择主券后查看已有关系。'}</p>
                  </div>
                </div>
                {mainCoupon ? <div className={styles.ruleScopeTags}>{existing.length > 0 ? existing.map((coupon) => <Tag key={coupon.id} type="neutral">{coupon.name}</Tag>) : <span className={styles.muted}>暂无其他关系</span>}</div> : null}
              </section> : null}
            </div>
          </>
          : <>
            <div className={styles.ruleRow}>
              <label className={`${styles.ruleLabel} ${styles.required}`} htmlFor="rule-group-name">组合名称：</label>
              <div className={styles.ruleValue}><Input ariaLabel="组合名称" className={styles.ruleInput} id="rule-group-name" maxLength={20} onValueChange={(value) => setDraft((current) => ({ ...current, groupName: value }))} placeholder="例如：多科连报优惠组" value={groupName} /></div>
            </div>
            <section className={styles.ruleScopeCard}>
              <div className={styles.ruleScopeCardHeader}>
                <div>
                  <h2>本次规则范围</h2>
                  <p>{`已选择 ${currentEntities.length} 张组合内优惠券${currentEntities.length < 2 ? '，至少选择2张优惠券。' : '。'}`}</p>
                </div>
                <Link aria-label="选择优惠券" className={styles.ruleScopeSelect} onClick={() => setPickContext('group')}>＋选择优惠券</Link>
              </div>
              {renderSelectedScope()}
            </section>
          </>}
      </div>
    </Dialog>

    <Dialog
      className={styles.compactConfirm}
      footer={<><Button onClick={() => setConfirmOpen(false)} type="default">继续编辑</Button><Button onClick={() => onOpenChange(false)}>放弃修改</Button></>}
      onOpenChange={(next) => { if (!next) setConfirmOpen(false); }}
      open={confirmOpen}
      size="sm"
      title="放弃未保存的修改？"
    >
      <p className={styles.confirmText}>当前规则修改尚未保存，关闭后将丢失。</p>
    </Dialog>

    <CouponPickerDialog
      base={pickerBase}
      excludeId={pickContext === 'specified' ? mainId ?? undefined : undefined}
      mode={pickContext === 'main' ? 'single' : 'multiple'}
      onConfirm={() => setPickContext(null)}
      onOpenChange={(next) => { if (!next) setPickContext(null); }}
      onSelectedChange={(ids) => setDraft((current) => {
        if (pickContext === 'main') return { ...current, mainId: ids[0] ?? null, specified: [] };
        if (pickContext === 'group') return { ...current, groupIds: ids };
        if (pickContext === 'specified') return { ...current, specified: ids };
        return current;
      })}
      open={pickContext !== null}
      selected={pickerSelected}
      title={pickContext === 'main' ? '选择主券' : pickContext === 'group' ? '选择优惠券' : '选择指定优惠券'}
    />
  </>;
}
